import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../components/FirebaseConfig';
import { useAuth } from '../components/AuthContext';
import { API_BASE } from '../apiSession';

// Exported for a page test that stands a flag in (JobAgent.test.js).
export const FeatureGateContext = createContext();

export const ADMIN_UID = 'MnwcS7G6ZGSsTQmkofjIl9Vu3jA3';

export const FeatureGateProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [flags, setFlags] = useState({
    // Back on (2026-08-31): the tracker is reborn as the Applied record, fed
    // by the extension watching confirmation pages — a signal we own, unlike
    // the Gmail inference it used to be.
    JOB_TRACKER: true,
    STORY_TRACKER: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFlags = async () => {
      if (!currentUser) return;
      try {
        const token = await currentUser.getIdToken();
        const res = await fetch(`${API_BASE}/api/config/features`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.ok) {
          console.log('[FeatureGate] Initial sync from backend:', Object.keys(data.flags));
          setFlags(prev => ({ ...prev, ...data.flags }));
        }
      } catch (err) {
        console.error('[FeatureGate] Backend sync failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFlags();
  }, [currentUser]);

  const uid = currentUser?.uid;
  useEffect(() => {
    // Signed out, there is nothing to gate: every gated route sits behind
    // PrivateRoute. Opening the listener anyway made each visitor to the
    // landing page start an anonymous Firestore channel that the rules
    // refuse, and print the refusal to their console (2026-10-19).
    if (!uid || !firestore) {
      setLoading(false);
      return undefined;
    }
    // Listen to the global config document (Best effort for real-time)
    const flagRef = doc(firestore, 'config', 'feature_flags');
    
    const unsubscribe = onSnapshot(flagRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('[FeatureGate] Real-time sync:', Object.keys(data));
        setFlags(data);
      }
      setLoading(false);
    }, (err) => {
      console.warn('[FeatureGate] Live listener limited (likely rules). Falling back to Backend sync.');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [uid]);

  const isFeatureEnabled = (flagName) => {
    // 1. Admin always has access
    if (currentUser?.uid === ADMIN_UID) return true;
    
    // 2. Otherwise check the real-time flag
    const feature = flags[flagName];
    
    // If it's a new object-style flag
    if (typeof feature === 'object' && feature !== null) {
      return !!feature.enabled;
    }
    
    // Fallback for simple boolean flags
    return !!feature;
  };

  return (
    <FeatureGateContext.Provider value={{ flags, isFeatureEnabled, loading }}>
      {children}
    </FeatureGateContext.Provider>
  );
};

/**
 * One flag, read where a provider may not be (a page kept running behind
 * another panel, a page test): `fallback` when there is none. The
 * Applications page links to Tracker only when JOB_TRACKER is on.
 */
export const useFeatureFlag = (flagName, fallback = false) => {
  const context = useContext(FeatureGateContext);
  return context ? context.isFeatureEnabled(flagName) : fallback;
};

export const useFeatureGate = () => {
  const context = useContext(FeatureGateContext);
  if (!context) {
    throw new Error('useFeatureGate must be used within a FeatureGateProvider');
  }
  return context;
};
